import { Formik, Form } from 'formik'
import { path, isEmpty, mergeRight, last, values as ramdaValues, prop } from 'ramda'
import { useEffect, useState, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import { useOpenInWindow } from 'use-open-window'
import * as Yup from 'yup'
import { Button } from '@/components/button'
import { DefaultButtonBar } from '@/components/button-bar'
import useProfile from '@/hooks/use-profile'
import { breakpoints } from '@/styles'
import { isNullOrEmpty } from '@/utils'
import { CardDetailsForm, BillingAddressForm } from '@/features/Payments/forms'
import PaymentMethodForm from './payment-method-form'
import Context from '../context'
import '@/utils/validation'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
`

const ContentWrapper = styled.div`
  max-width: 700px;
  width: calc(100% - 40px);
  margin: 0 20px 100px;
  text-align: center;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    width: calc(100% - 20px);
    margin: 0 10px 70px;
  }
`

const PaymentMethod = ({ setCurrentStep, handleBack }) => {
  const { t } = useTranslation()
  const { formData, setFormData, paymentData } = useContext(Context)
  const dispatch = useDispatch()
  const userProfile = useProfile()
  const hasAddress = userProfile.has_billing_address
  const addressData = useSelector(path(['settlements', 'billing_address']))
  const [coinbaseUrl, setCoinbaseUrl] = useState(null)

  const [cardInfoValid, setCardInfoValid] = useState(false)
  const [cardMethodValid, setCardMethodValid] = useState(false)
  const [acceptedTerms, setAcceptedTerms] = useState(false)

  const windowOptions = {
    centered: true,
    specs: {
      width: 550,
      height: 650
    }
  }
  const [handleWindowOpen] = useOpenInWindow(windowOptions)
  const hasPaymentTransactions = !isNullOrEmpty(paymentData.payment_transactions)

  useEffect(() => {
    if (hasAddress && !addressData) dispatch.settlements.getBillingAddress()
  }, [hasAddress, addressData, dispatch.settlements])

  const initialValues = isEmpty(formData)
    ? {
        payment_method: '',
        promo_code: '',
        first_name: '',
        last_name: '',
        card_number: '',
        expiration_date: '',
        security_code: '',
        street1: addressData?.street1 || '',
        street2: addressData?.street2 || '',
        city: addressData?.city || '',
        state: addressData?.state || '',
        country: addressData?.country || 'ca',
        zip: addressData?.zip || '',
        save_billing_address: false
      }
    : { ...formData }

  const validationSchema = Yup.object().shape({
    payment_method: Yup.string().required(),
    promo_code: Yup.string(),
    first_name: Yup.string().when('payment_method', {
      is: 'crypto',
      then: Yup.string(),
      otherwise: Yup.string().required()
    }),
    last_name: Yup.string().when('payment_method', {
      is: 'crypto',
      then: Yup.string(),
      otherwise: Yup.string().required()
    }),
    expiration_date: Yup.string().when('payment_method', {
      is: 'crypto',
      then: Yup.string(),
      otherwise: Yup.string().length(4).required()
    }),
    security_code: Yup.string().when('payment_method', {
      is: 'crypto',
      then: Yup.string(),
      otherwise: Yup.string().min(3).max(4).required()
    }),
    card_number: Yup.string().when('payment_method', {
      is: 'crypto',
      then: Yup.string(),
      otherwise: Yup.string().required()
    }),
    street1: Yup.string().when('payment_method', {
      is: 'stripe',
      then: Yup.string().required()
    }),
    street2: Yup.string(),
    city: Yup.string().when('payment_method', {
      is: 'stripe',
      then: Yup.string().required()
    }),
    country: Yup.string().when('payment_method', {
      is: 'stripe',
      then: Yup.string().required()
    }),
    state: Yup.string().when('payment_method', {
      is: 'stripe',
      then: Yup.string().required()
    }),
    zip: Yup.string().when('payment_method', {
      is: 'stripe',
      then: Yup.string().required()
    }),
    save_billing_address: Yup.bool().when('payment_method', {
      is: 'stripe',
      then: Yup.bool().required()
    })
  })

  const onSubmitCrypto = async (e, values) => {
    if (hasPaymentTransactions) {
      const transactionUrl = prop(
        'coinbase_hosted_url',
        last(ramdaValues(paymentData.payment_transactions))
      )
      handleWindowOpen(e, {
        url: transactionUrl
      })
    } else if (coinbaseUrl) {
      handleWindowOpen(e, {
        url: coinbaseUrl
      })
    } else {
      const { response, body } = await dispatch.settlements.makePayment({
        payment_method: 'crypto',
        crypto_currency: 'bitcoin',
        paymentRequestId: paymentData.id,
        promo_code: values.promo_code
      })
      if (response.ok) {
        setCoinbaseUrl(body.transaction_info.hosted_url)
        handleWindowOpen(e, {
          url: body.transaction_info.hosted_url
        })
      }
    }
  }

  const onSubmit = async (values, { setSubmitting }) => {
    setFormData(mergeRight(formData, values))
    setCurrentStep('details')
    window.scrollTo(0, 0)
    setSubmitting(false)
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      enableReinitialize
    >
      {({ values, setFieldValue, isValid, isSubmitting }) => (
        <Form data-testid="payment-method-form" className="flex-full">
          <Wrapper>
            <ContentWrapper className="mb-100">
              <PaymentMethodForm
                values={values}
                setFieldValue={setFieldValue}
                acceptedTerms={acceptedTerms}
                setAcceptedTerms={setAcceptedTerms}
              />
              {(values.payment_method === 'stripe' || values.payment_method === 'upop') && (
                <CardDetailsForm
                  values={values}
                  setCardInfoValid={setCardInfoValid}
                  setCardMethodValid={setCardMethodValid}
                  allowUnionPayOnStripe
                />
              )}
              {values.payment_method === 'stripe' && (
                <BillingAddressForm values={values} setFieldValue={setFieldValue} />
              )}
            </ContentWrapper>
          </Wrapper>
          <DefaultButtonBar handleBack={handleBack}>
            {values.payment_method === 'crypto' ? (
              <Button
                theme="primary"
                size="small"
                disabled={!isValid || isSubmitting || !acceptedTerms}
                onClick={e => onSubmitCrypto(e, values)}
                id="web-advertise-listing-form-check-out-to-pay"
              >
                {t('r.payment.edit.make_payment_pay_now.button', 'Pay Now')}
              </Button>
            ) : (
              <Button
                theme="primary"
                size="small"
                type="submit"
                disabled={!cardInfoValid || !cardMethodValid || !isValid || isSubmitting}
              >
                {t('common.next', 'Next')}
              </Button>
            )}
          </DefaultButtonBar>
        </Form>
      )}
    </Formik>
  )
}

export default PaymentMethod
